import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { weightedSplit } from '@tips-crm/shared';
import {
  distributeLeads,
  fetchRepPerformance,
  fetchSellers,
  setUserSeller,
  type DistributionResult,
  type RepPerformance,
} from '../../api/endpoints.js';
import { errorMessage } from '../../api/client.js';
import { useAuth } from '../../auth/AuthContext.js';
import { PageHeader } from '../../components/PageHeader.js';
import { StatCard } from '../../components/StatCard.js';
import { ErrorState, Loading } from '../../components/States.js';

type SortKey =
  | 'display_name'
  | 'completed'
  | 'total_calls'
  | 'dispositioned'
  | 'abandoned'
  | 'avg_handle_seconds'
  | 'open_leads'
  | 'ai_score'
  | 'avg_talk_ratio';

// Rep row enriched with a single AI coaching score (mean of the scorecard dims).
type RepRow = RepPerformance & { ai_score: number | null };

function aiScore(r: RepPerformance): number | null {
  const dims = [r.avg_discovery, r.avg_objection_handling, r.avg_closing, r.avg_next_steps].filter(
    (v): v is number => v !== null,
  );
  if (dims.length === 0) return null;
  return dims.reduce((a, b) => a + b, 0) / dims.length;
}

function formatHandleTime(seconds: number): string {
  if (!seconds || seconds < 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function LeadDistributionPage() {
  const queryClient = useQueryClient();
  const { hasRole } = useAuth();
  const isPlatformAdmin = hasRole('platform_admin');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('completed');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  // Which reps are selected to receive leads, and how many each should get.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [result, setResult] = useState<DistributionResult | null>(null);

  const perfQ = useQuery({
    queryKey: ['rep-performance', from, to],
    queryFn: () => fetchRepPerformance({ from: from || undefined, to: to || undefined }),
  });

  const distMut = useMutation({
    mutationFn: distributeLeads,
    onSuccess: (res) => {
      setResult(res);
      void queryClient.invalidateQueries({ queryKey: ['rep-performance'] });
      void queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });

  // Reps can only receive leads from their own seller, so a rep without a seller can't
  // be distributed to at all. Platform admins can fix that inline from the Seller column.
  const sellersQ = useQuery({
    queryKey: ['sellers'],
    queryFn: fetchSellers,
    enabled: isPlatformAdmin,
  });
  const sellerMut = useMutation({
    mutationFn: ({ userId, sellerId }: { userId: string; sellerId: string | null }) =>
      setUserSeller(userId, sellerId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['rep-performance'] });
      void queryClient.invalidateQueries({ queryKey: ['assignable-users'] });
    },
  });

  const reps = perfQ.data?.reps ?? [];
  const pool = perfQ.data?.unassigned_pool ?? 0;

  const sortedReps = useMemo(() => {
    const copy: RepRow[] = reps.map((r) => ({ ...r, ai_score: aiScore(r) }));
    copy.sort((a, b) => {
      // Reps without AI data sort below any measured value.
      const av = a[sortKey] ?? -1;
      const bv = b[sortKey] ?? -1;
      let cmp: number;
      if (typeof av === 'number' && typeof bv === 'number') cmp = av - bv;
      else cmp = String(av).localeCompare(String(bv));
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [reps, sortKey, sortDir]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir(key === 'display_name' ? 'asc' : 'desc');
    }
  };

  const toggleSelect = (userId: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });

  const selectTopN = (n: number) => {
    // Top N by the current sort (defaults to completed desc).
    setSelected(new Set(sortedReps.slice(0, n).map((r) => r.user_id)));
  };

  const requestedTotal = useMemo(
    () => [...selected].reduce((sum, id) => sum + (counts[id] ?? 0), 0),
    [selected, counts],
  );

  // Suggest a performance-weighted split of `target` leads across the selected reps.
  const applyWeightedSplit = (target: number) => {
    const chosen = sortedReps.filter((r) => selected.has(r.user_id));
    if (chosen.length === 0) return;
    const capped = Math.min(target, pool);
    const split = weightedSplit(
      chosen.map((r) => r.score),
      capped,
    );
    setCounts((prev) => {
      const next = { ...prev };
      chosen.forEach((r, i) => {
        next[r.user_id] = split[i] ?? 0;
      });
      return next;
    });
  };

  const setCount = (userId: string, value: number) =>
    setCounts((prev) => ({ ...prev, [userId]: Math.max(0, Math.floor(value || 0)) }));

  const submit = () => {
    const assignments = [...selected]
      .map((id) => ({ user_id: id, count: counts[id] ?? 0 }))
      .filter((a) => a.count > 0);
    if (assignments.length === 0) return;
    setResult(null);
    distMut.mutate(assignments);
  };

  const sortArrow = (key: SortKey) =>
    sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

  return (
    <div className="page">
      <PageHeader
        title="Lead Distribution"
        subtitle="Rank reps by performance and allocate unassigned leads in bulk."
        actions={
          <div className="filter-row">
            <label className="field field--inline">
              <span className="field__label">From</span>
              <input
                className="field__input"
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
            </label>
            <label className="field field--inline">
              <span className="field__label">To</span>
              <input
                className="field__input"
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
            </label>
          </div>
        }
      />

      {perfQ.isLoading && <Loading />}
      {perfQ.isError && <ErrorState error={perfQ.error} onRetry={() => perfQ.refetch()} />}

      {perfQ.data && (
        <>
          <section className="stat-grid">
            <StatCard label="Unassigned leads" value={pool} hint="available to distribute" />
            <StatCard label="Reps" value={reps.length} />
            <StatCard label="Selected" value={selected.size} />
            <StatCard label="Requested" value={requestedTotal} hint="leads to assign" />
          </section>

          <section className="card">
            <div className="toolbar" style={{ gap: 8, flexWrap: 'wrap' }}>
              <button type="button" className="btn btn--ghost" onClick={() => selectTopN(3)}>
                Select top 3
              </button>
              <button type="button" className="btn btn--ghost" onClick={() => selectTopN(5)}>
                Select top 5
              </button>
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => setSelected(new Set())}
              >
                Clear
              </button>
              <span style={{ flex: 1 }} />
              <button
                type="button"
                className="btn"
                disabled={selected.size === 0 || pool === 0}
                onClick={() => applyWeightedSplit(pool)}
                title="Distribute the whole unassigned pool, weighted by performance"
              >
                Auto-weight whole pool ({pool})
              </button>
            </div>

            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th style={{ width: 40 }} aria-label="Select" />
                    <th className="th-sort" onClick={() => toggleSort('display_name')}>
                      Rep{sortArrow('display_name')}
                    </th>
                    <th>Seller</th>
                    <th className="th-sort" onClick={() => toggleSort('completed')}>
                      Completed{sortArrow('completed')}
                    </th>
                    <th className="th-sort" onClick={() => toggleSort('total_calls')}>
                      Total calls{sortArrow('total_calls')}
                    </th>
                    <th className="th-sort" onClick={() => toggleSort('dispositioned')}>
                      Dispositioned{sortArrow('dispositioned')}
                    </th>
                    <th className="th-sort" onClick={() => toggleSort('abandoned')}>
                      Abandoned{sortArrow('abandoned')}
                    </th>
                    <th className="th-sort" onClick={() => toggleSort('avg_handle_seconds')}>
                      AHT{sortArrow('avg_handle_seconds')}
                    </th>
                    <th className="th-sort" onClick={() => toggleSort('open_leads')}>
                      Open leads{sortArrow('open_leads')}
                    </th>
                    <th className="th-sort" onClick={() => toggleSort('ai_score')} title="Mean of AI coaching scores (discovery, objection handling, closing, next steps) over analyzed calls">
                      AI score{sortArrow('ai_score')}
                    </th>
                    <th className="th-sort" onClick={() => toggleSort('avg_talk_ratio')} title="Average share of the conversation the rep spent talking">
                      Talk ratio{sortArrow('avg_talk_ratio')}
                    </th>
                    <th title="Objections handled well / objections encountered (AI-graded)">Objections</th>
                    <th style={{ width: 110 }}>Assign</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedReps.map((r: RepRow) => {
                    const isSel = selected.has(r.user_id);
                    return (
                      <tr
                        key={r.user_id}
                        style={isSel ? { background: 'var(--primary-soft)' } : undefined}
                      >
                        <td>
                          <input
                            type="checkbox"
                            checked={isSel}
                            onChange={() => toggleSelect(r.user_id)}
                            aria-label={`Select ${r.display_name}`}
                          />
                        </td>
                        <td>
                          {r.display_name}
                          <div className="small muted">{r.email}</div>
                        </td>
                        <td>
                          {r.seller_name ??
                            (isPlatformAdmin ? (
                              // A rep without a seller can't receive leads — let the
                              // platform admin fix it right here instead of erroring later.
                              <select
                                className="field__input"
                                value=""
                                disabled={sellerMut.isPending}
                                onChange={(e) => {
                                  if (e.target.value) {
                                    sellerMut.mutate({ userId: r.user_id, sellerId: e.target.value });
                                  }
                                }}
                                aria-label={`Set seller for ${r.display_name}`}
                              >
                                <option value="">No seller — set…</option>
                                {(sellersQ.data ?? []).map((s) => (
                                  <option key={s.id} value={s.id}>
                                    {s.name}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <span className="form-error">No seller</span>
                            ))}
                        </td>
                        <td>{r.completed}</td>
                        <td>{r.total_calls}</td>
                        <td>{r.dispositioned}</td>
                        <td>{r.abandoned}</td>
                        <td>{formatHandleTime(r.avg_handle_seconds)}</td>
                        <td>{r.open_leads}</td>
                        <td>{r.ai_score !== null ? `${r.ai_score.toFixed(1)}/5` : '—'}</td>
                        <td>
                          {r.avg_talk_ratio !== null ? `${Math.round(r.avg_talk_ratio * 100)}%` : '—'}
                        </td>
                        <td>
                          {r.objection_count > 0
                            ? `${r.objections_well_handled}/${r.objection_count}`
                            : '—'}
                        </td>
                        <td>
                          <input
                            className="field__input"
                            type="number"
                            min={0}
                            style={{ width: 90 }}
                            disabled={!isSel}
                            value={isSel ? counts[r.user_id] ?? 0 : ''}
                            onChange={(e) => setCount(r.user_id, Number(e.target.value))}
                          />
                        </td>
                      </tr>
                    );
                  })}
                  {sortedReps.length === 0 && (
                    <tr>
                      <td colSpan={10} className="muted" style={{ textAlign: 'center' }}>
                        No reps found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="toolbar" style={{ marginTop: 12 }}>
              {requestedTotal > pool && (
                <span className="form-error">
                  Requested {requestedTotal} exceeds the {pool} available — extra reps will get
                  fewer than requested.
                </span>
              )}
              <span style={{ flex: 1 }} />
              <button
                type="button"
                className="btn btn--primary"
                disabled={requestedTotal === 0 || distMut.isPending}
                onClick={submit}
              >
                {distMut.isPending ? 'Distributing…' : `Distribute ${requestedTotal} leads`}
              </button>
            </div>

            {sellerMut.isError && (
              <p className="form-error">{errorMessage(sellerMut.error)}</p>
            )}
            {distMut.isError && (
              <p className="form-error">{errorMessage(distMut.error)}</p>
            )}
            {result && (
              <p
                style={{
                  marginTop: 12,
                  color: 'var(--success)',
                  fontWeight: 600,
                }}
              >
                Assigned {result.total_assigned} of {result.total_requested} requested
                {result.pool_exhausted ? ' (unassigned pool exhausted).' : '.'}
              </p>
            )}
          </section>
        </>
      )}
    </div>
  );
}
